import { CustomerOrganizationCsv, VCOOrganization, VCOProject } from './CustomProjectModels';
import { Form, Spinner } from 'react-bootstrap';
import React, { FunctionComponent, useContext, useEffect, useMemo, useState } from 'react';
import { Slide, toast } from 'react-toastify';

import ApiHelper from '../../../Shared/ApiHelper';
import SaveButton from '../../../Shared/components/SaveButton';
import { UserProfileContext } from '../../../Shared/contexts/UserProfile';
import { useMsal } from '@azure/msal-react';

export interface OrganizationEditFormProps {
    vcoProject: VCOProject;
    batchKey: number;
}

const OrganizationEditForm: FunctionComponent<OrganizationEditFormProps> = (props: OrganizationEditFormProps) => {
    const { vcoProject, batchKey } = props;
    const userProfile = useContext(UserProfileContext);

    const [customOrganizationCsv, setCustomOrganizationCsv] = useState<CustomerOrganizationCsv>({
        batchKey: 0,
        sourceSystemShortName: '',
        organizationID: '',
    });
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const api = useMemo(() => new ApiHelper(), []);
    const { instance } = useMsal();

    useEffect(() => {
        if (isLoading && vcoProject.projectKey && batchKey) {
            api.callApi(
                instance,
                [process.env.REACT_APP_B2C_SCOPE ?? ''],
                process.env.REACT_APP_NET_API_URL + `/CustomProject/organizations/${vcoProject.organizationKey}`,
                'GET'
            )
                .then(async (result: Response) => {
                    const organization: VCOOrganization = await result.json();

                    const customOrganization: CustomerOrganizationCsv = {
                        batchKey: batchKey,
                        sourceSystemShortName:
                            userProfile.sourceSystems.find((s) => s.sourceSystemKey === vcoProject.sourceSystemKey)
                                ?.sourceSystemShortName ?? '',
                        organizationID: organization.organizationID ?? vcoProject.organizationID ?? '',
                        victimServiceProvider: organization.victimServiceProviderKey?.toString(),
                        dateCreated: organization.dateCreated,
                        dateUpdated: organization.dateUpdated,
                        dateDeleted: organization.dateDeleted,
                        userID: organization.userID,
                    };
                    setCustomOrganizationCsv(customOrganization);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    }, [
        api,
        batchKey,
        instance,
        isLoading,
        userProfile.sourceSystems,
        vcoProject.organizationID,
        vcoProject.organizationKey,
        vcoProject.projectKey,
        vcoProject.sourceSystemKey,
    ]);

    return (
        <div className="project-card mb-5">
            <div className="project-card-header">
                <h3>Organization CSV Data</h3>
                {!!customOrganizationCsv.batchKey && (
                    <SaveButton
                        isSaving={isSaving}
                        isDisabled={!customOrganizationCsv.victimServiceProvider}
                        onClick={() => {
                            setIsSaving(true);

                            api.callApi(
                                instance,
                                [process.env.REACT_APP_B2C_SCOPE ?? ''],
                                process.env.REACT_APP_NET_API_URL + `/CustomProject/organization`,
                                'POST',
                                JSON.stringify(customOrganizationCsv)
                            )
                                .then(async (_result: Response) => {
                                    toast.success('Organization Data Saved Successfully', {
                                        position: 'top-right',
                                        autoClose: 5000,
                                        transition: Slide,
                                        draggable: false,
                                        closeOnClick: false,
                                        theme: 'colored',
                                    });
                                })
                                .finally(() => {
                                    setIsSaving(false);
                                });
                        }}
                    />
                )}
            </div>
            {isLoading ? (
                <Spinner
                    className="centered-spinner"
                    variant="primary"
                    size="sm"
                    animation="border"
                    data-testid="projectEditFormSpinner"
                />
            ) : (
                <div className="project-card-body">
                    <Form className="card-form">
                        <Form.Group className="card-form-group">
                            <Form.Label>Victim Service Provider Key*</Form.Label>
                            <Form.Control
                                data-testid="vspInput"
                                placeholder="Enter Victim Service Provider Key"
                                isInvalid={!customOrganizationCsv.victimServiceProvider}
                                value={customOrganizationCsv?.victimServiceProvider}
                                onChange={(event) => {
                                    const numberValue = parseInt(event.target.value);
                                    const isNumber = !isNaN(numberValue);

                                    if (isNumber) {
                                        setCustomOrganizationCsv((prev) => {
                                            return { ...prev, victimServiceProvider: numberValue.toString() };
                                        });
                                    } else if (!event.target.value) {
                                        setCustomOrganizationCsv((prev) => {
                                            return { ...prev, victimServiceProvider: undefined };
                                        });
                                    }
                                }}
                            />
                        </Form.Group>
                    </Form>
                </div>
            )}
        </div>
    );
};

export default OrganizationEditForm;
